/**
 * Le fil de la séance du masteur : la priorité d'un signal, son action, et l'ordre de la liste.
 *
 * Port littéral de l'ancien panneau d'événements (`tutorEventsPanel/eventsCatalog.js`) :
 * `EVENT_CATALOG[type].priority` (1, 3, 5) devient `urgent`, `normal`, `low` ; `getEffectiveCta` devient
 * `tutorFeedAction` ; `sortEventsByPriority` devient `tutorFeedPriorityRank`. Une seule action par carte, et
 * « Envoyer » le champion seulement quand le masteur est en Dialogue avec un autre élève : ailleurs, il ne veut
 * rien dire (Wajih, round 3 du live, 02/11).
 *
 * Le champion qui aide en séance est nommé par son rôle : son prénom arrive en entrée (`helperName`),
 * du monde de jeu de l'espace, jamais écrit ici.
 *
 * Des signaux n'existaient pas dans l'ancien panneau : le résumé du champion (une entrée par résumé,
 * il était dans sa feuille), et ceux de la cible, non livrés — sa proposition, la relance qu'il souffle
 * au masteur en Dialogue (`normal` : à lire maintenant ; la croix l'écarte), celle qu'il fait à l'élève
 * en Concentré (`low`, « Annuler la relance »). En `low` comme « discute avec » lui, sauf un résumé où
 * il demande le masteur (`needsHelp`) : `urgent`.
 */
import {
    type UITutorFeedAction,
    type UITutorFeedItem,
    type UITutorFeedPriority,
    type UITutorFeedSignal,
} from "../types/tutorLive"

const PRIORITY: Record<UITutorFeedSignal, UITutorFeedPriority> = {
    hand_raised: "urgent",
    silence: "urgent",
    out_of_app: "normal",
    exercise_done: "normal",
    auto_activity: "normal",
    sam_ongoing: "low",
    sam_summary: "low",
    sam_proposal: "low",
    sam_whisper: "normal",
    sam_nudge: "low",
}

const RANK: Record<UITutorFeedPriority, number> = { urgent: 1, normal: 3, low: 5 }

/** La priorité d'un signal (l'ancien `EVENT_CATALOG[type].priority`). */
export function tutorFeedPriority(
    signal: UITutorFeedSignal,
    options: { needsHelp?: boolean } = {}
): UITutorFeedPriority {
    if (signal === "sam_summary" && options.needsHelp === true) return "urgent"
    return PRIORITY[signal]
}

/** Les signaux que le champion peut prendre avant que le masteur ne réponde (l'ancien `SAM_ELIGIBLE`). */
const SAM_ELIGIBLE: ReadonlySet<UITutorFeedSignal> = new Set(["hand_raised", "silence"])

export type TutorFeedActionInput = {
    signal: UITutorFeedSignal
    studentId: string
    firstname: string
    /** Le prénom du champion qui aide en séance, du monde de jeu de l'espace. */
    helperName: string
    /** Le masteur a envoyé le champion à l'élève pour ce signal. */
    samSent?: boolean
    /** Quatre minutes sans réponse. */
    escalated?: boolean
    /** L'élève avec qui le masteur est en Dialogue en ce moment ; absent ou `null` : personne. */
    dialogueWithStudentId?: string | null
    /** Le masteur est sur le web (« Parler à » un élève sorti de l'appli ; au natif, « Notifier »). */
    isWeb: boolean
}

/**
 * L'action de la carte, une seule (l'ancien `getEffectiveCta`). `passive` : la carte dit ce qui se passe
 * (« … aide Léa ») et ouvre le résumé, elle ne demande rien. Absente : la carte informe.
 *
 * Le libellé est court (Wajih, 05/11 : « des libellés plus courts ») : la phrase de la rangée commence par le prénom
 * en gras, le bouton ne le redit pas. Le lecteur d'écran, qui lit le bouton seul, reçoit la phrase entière.
 */
export function tutorFeedAction(input: TutorFeedActionInput): UITutorFeedAction | undefined {
    const { signal, studentId, firstname, helperName, samSent, escalated, dialogueWithStudentId, isWeb } = input

    if (SAM_ELIGIBLE.has(signal)) {
        if (samSent === true && escalated !== true) {
            return { id: "read_summary", label: `${helperName} aide ${firstname}`, passive: true }
        }
        if (offersHelper(input)) {
            return {
                id: "send_sam",
                label: `Envoyer ${helperName}`,
                accessibilityLabel: `Envoyer ${helperName} à ${firstname}`,
            }
        }
        return talkTo(firstname)
    }

    switch (signal) {
        case "out_of_app":
            if (!isWeb) return { id: "notify", label: "Notifier", accessibilityLabel: `Notifier ${firstname}` }
            // Déjà en Dialogue avec lui : « Parler » ne ferait rien de plus, la carte informe.
            return dialogueWithStudentId === studentId ? undefined : talkTo(firstname)
        case "exercise_done":
            return { id: "open_work", label: "Voir le rendu", accessibilityLabel: `Voir le rendu de ${firstname}` }
        case "sam_summary":
            return { id: "read_summary", label: "Lire", accessibilityLabel: "Lire le résumé" }
        case "sam_proposal":
            return { id: "add_followup", label: "Ajouter au suivi" }
        case "sam_nudge":
            return { id: "cancel_nudge", label: "Annuler", accessibilityLabel: "Annuler la relance" }
        default:
            return undefined
    }
}

/**
 * Les gestes d'une carte : son action (`tutorFeedAction`) et, à côté de « Envoyer <champion> », le second : le masteur
 * qui parle à un autre élève peut aussi prendre la main lui-même (Wajih, 05/11 : « il faut aussi pouvoir lui
 * parler »). « Parler » le passe en Dialogue (et l'autre en Concentré). À étaler dans la carte : les deux clés sont
 * toujours rendues, pour qu'une carte recalculée sans envoi ne garde pas le « Parler » d'avant.
 */
export function tutorFeedActions(input: TutorFeedActionInput): Pick<UITutorFeedItem, "action" | "secondaryAction"> {
    const action = tutorFeedAction(input)
    return action?.id === "send_sam"
        ? { action, secondaryAction: talkTo(input.firstname) }
        : { action, secondaryAction: undefined }
}

/**
 * Le champion s'occupe de la carte (son action passive, « … aide Léa ») : elle n'attend plus le masteur. « À traiter »
 * et le rail replié la taisent ; elle vit sur la tuile.
 */
export function helperHandles(item: Pick<UITutorFeedItem, "action">): boolean {
    return item.action?.passive === true
}

/** Une carte demande une décision au masteur : une action, que le champion ne porte pas (la croix, les comptes). */
export function awaitsTutor(item: Pick<UITutorFeedItem, "action">): boolean {
    return item.action != null && !helperHandles(item)
}

const talkTo = (firstname: string): UITutorFeedAction => ({
    id: "talk",
    label: "Parler",
    accessibilityLabel: `Parler à ${firstname}`,
})

/** Une main ou un silence pas encore confiés au champion, pendant que le masteur parle à un autre élève. */
function offersHelper(input: TutorFeedActionInput): boolean {
    const { signal, studentId, samSent, escalated, dialogueWithStudentId } = input
    return (
        SAM_ELIGIBLE.has(signal) &&
        samSent !== true &&
        escalated !== true &&
        dialogueWithStudentId != null &&
        dialogueWithStudentId !== studentId
    )
}

/**
 * Le rang d'une priorité (l'ancien `sortEventsByPriority` : 1, 3, 5) : l'urgente d'abord. Le bac « À décider »
 * de la session se range par lui (`tutorSessionTimeline`).
 */
export function tutorFeedPriorityRank(priority: UITutorFeedPriority): number {
    return RANK[priority]
}

/** Le temps écoulé d'une carte : « 42s », puis « 3 min ». */
export function formatElapsed(startedAt: number, now: number): string {
    const seconds = Math.max(0, Math.floor((now - startedAt) / 1000))
    if (seconds < 60) return `${seconds}s`
    return `${Math.floor(seconds / 60)} min`
}

/** L'instant où `formatElapsed` change : à la seconde pendant la première minute, puis à la minute. */
export function nextElapsedChange(startedAt: number, now: number): number {
    const age = Math.max(0, now - startedAt)
    const step = age < 60 * 1000 ? 1000 : 60 * 1000
    return startedAt + (Math.floor(age / step) + 1) * step
}
